import { EntityType, Task, User } from "@prisma/client";
import { IconAlarm, IconCircle, IconCircleCheckFilled, IconTargetArrow, IconTrash, IconUser } from "@tabler/icons-react";
import classNames from "classnames";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  formatInboxEntityLabel,
  getInboxTaskAssigneeLabel,
  getInboxTaskCreatedByLabel,
  getInboxTaskDue,
  getInboxTaskDueColors,
  getInboxTaskInitials,
} from "~/components/action-center/inbox-task-row";
import { DebouncedTextInput } from "~/components/DebouncedTextInput";
import { DateInputValueFormat } from "~/components/datetime";
import { OrgLink } from "~/components/OrgLink";
import { useUser } from "~/contexts/userProvider";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Container,
  DatePickerInput,
  Flex,
  mantineColorRamps,
  Select,
  showNotification,
  Text,
  Textarea,
  Tooltip,
  tokens,
  useClickOutside,
  useDebouncedValue,
  useHover,
} from "~/ui";
import { api } from "~/utils/api";

import { TaskWithAssignedUser, UpdateTaskSchemaType } from "../schema";
import styles from "./TaskCard.module.css";

type TaskCardProps = {
  isEditable?: boolean;
  task: TaskWithAssignedUser;
  onUpdate: () => void;
  /**
   * Default `true` preserves this component's own card surface (shadow/border/padding/margin) for
   * any standalone caller. `false` is for `InboxMessageRow` (SC-3392 round 18 restore, SC-3700
   * redesign): the card renders as a flat, full-bleed Inbox row — leading complete toggle, inline-
   * editable title/description/due date/assignee, hover-delete — and owns that row's frame (padding,
   * divider, `position: relative`), because its delete button and confirm overlay are absolutely
   * positioned in the row's padding and must hover and resolve against the full row.
   */
  withChrome?: boolean;
};

type ContextualInputProps = {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
};

type AssignedUsersSelectProps = Omit<ContextualInputProps, "onChange"> & {
  onChange: (value: string, user: Partial<User> | null) => void;
};

type DueDateInputProps = Omit<ContextualInputProps, "value" | "onChange"> & {
  value: Date | null;
  onChange: (value: Date) => void;
};

type UserDisplayInputProps = {
  userId?: string | null;
  avatar?: string | null;
  label?: string;
  prefix?: string;
  isCompleted?: boolean;
};

type ContextBadgeProps = {
  entityId?: string;
  entityType: EntityType;
};

type ContextLink = {
  tooltip: string;
  type: EntityType;
  id?: boolean;
  generateLink: (id?: string) => string;
};

const contextLinks: ContextLink[] = [
  {
    tooltip: "Go to Opportunity",
    type: EntityType.OPPORTUNITY,
    id: true,
    generateLink: id => `/opportunities/${id}`,
  },
];

const AssignedUserSelect = (props: AssignedUsersSelectProps): JSX.Element => {
  const users = api.users.searchUsersWithRoles.useQuery({});
  const ref = useClickOutside(props.onClose);

  const selectUsers = users.data
    ? users.data.map(x => ({
        label: `${x.firstName} ${x.lastName}`,
        value: x.id,
      }))
    : [];

  const handleSelectUser = (value: string) => {
    if (!users.data) {
      return;
    }
    const selectedUser = users.data.find(x => x.id === value);
    if (!selectedUser) {
      return;
    }
    props.onChange(value, selectedUser);
  };

  return (
    <Flex ref={ref}>
      <Select
        clearable
        data={selectUsers}
        initiallyOpened={true}
        maxDropdownHeight={150}
        placeholder="Select a User to assign"
        size="xs"
        value={props.value}
        withinPortal
        onChange={handleSelectUser}
      />
    </Flex>
  );
};

const DueDateInput = (props: DueDateInputProps): JSX.Element => {
  const ref = useClickOutside(props.onClose);

  const value = props.value ? new Date(props.value) : null;

  return (
    <Flex ref={ref}>
      <DatePickerInput
        clearable
        placeholder="When is this Task due?"
        valueFormat={DateInputValueFormat}
        size="xs"
        value={value}
        onChange={props.onChange}
      />
    </Flex>
  );
};

const TitleUpdateInput = (props: ContextualInputProps): JSX.Element => (
  <DebouncedTextInput
    autoFocus
    placeholder="ie: Call John"
    wait={250}
    size="xs"
    value={props.value}
    onChange={e => props.onChange(e.target.value)}
    onBlur={props.onClose}
  />
);

const DescriptionUpdateInput = (props: ContextualInputProps): JSX.Element => {
  const hasMounted = useRef(false);
  const [value, setValue] = useState<string>(props.value);
  const [debounced] = useDebouncedValue(value, 250);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    props.onChange(debounced);
  }, [debounced]);

  return (
    <Textarea
      autoFocus
      autosize
      minRows={3}
      maxRows={6}
      placeholder="(Optional) ie: Don't forget to bring your wallet!"
      rows={6}
      size="xs"
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={props.onClose}
    />
  );
};

const findContextLink = (props: ContextBadgeProps): ContextLink | undefined =>
  contextLinks.find(x => {
    if (!!props.entityId && !!props.entityType) {
      return x.type === props.entityType && !!x.id;
    }
    return x.type === props.entityType;
  });

const ContextBadge = (props: ContextBadgeProps): JSX.Element => {
  const contextLink = findContextLink(props);

  const badgeComponent = <Badge size="xs">{props.entityType}</Badge>;

  return contextLink ? (
    <Tooltip label="Go to Opportunity" arrowSize={6} arrowRadius={4} position="left" withArrow>
      <OrgLink href={contextLink.generateLink(props.entityId)}>{badgeComponent}</OrgLink>
    </Tooltip>
  ) : (
    badgeComponent
  );
};

/** The Inbox row's context: a text link with a target icon for a record we can open, plain text otherwise. */
const InboxTaskContext = (props: ContextBadgeProps): JSX.Element => {
  const contextLink = findContextLink(props);
  const label = formatInboxEntityLabel(props.entityType);

  if (!contextLink) {
    return (
      <Text c="inherit" component="span" fz="xs">
        {label}
      </Text>
    );
  }

  return (
    <Tooltip label={contextLink.tooltip} arrowSize={6} arrowRadius={4} position="left" withArrow>
      <OrgLink className={styles.contextLink} href={contextLink.generateLink(props.entityId)}>
        <IconTargetArrow size={13} />
        <Text c="inherit" component="span" fw={500} fz="xs">
          {label}
        </Text>
      </OrgLink>
    </Tooltip>
  );
};

type InboxTaskMetaProps = {
  task: TaskWithAssignedUser;
  enableEditing: boolean;
  isUpdatingAssignedTo: boolean;
  onStartAssigning: () => void;
  onStopAssigning: () => void;
  onAssign: AssignedUsersSelectProps["onChange"];
};

/** The Inbox row's meta line (handoff §3): assignee pill, "Created by", context link. */
const InboxTaskMeta = (props: InboxTaskMetaProps): JSX.Element => {
  const viewer = useUser();
  const { task } = props;
  const assignee = task.assignedTo ? { id: task.assignedTo.id, name: `${task.assignedTo.firstName} ${task.assignedTo.lastName}` } : null;
  const creator = { id: task.createdBy.id, name: `${task.createdBy.firstName} ${task.createdBy.lastName}` };

  return (
    <div className={styles.meta}>
      {props.isUpdatingAssignedTo ? (
        <AssignedUserSelect value={task.assignedToId ?? ""} onClose={props.onStopAssigning} onChange={props.onAssign} />
      ) : (
        <div
          className={classNames(styles.assignee, props.enableEditing && styles.assigneeEditable)}
          onClick={() => {
            if (!props.enableEditing) {
              return;
            }
            props.onStartAssigning();
          }}
        >
          {assignee ? (
            <Avatar size={18} src={task.assignedTo?.profileImageUrl}>
              {getInboxTaskInitials(assignee.name)}
            </Avatar>
          ) : (
            <IconUser size={14} />
          )}
          <Text c="inherit" component="span" fz="xs">
            {getInboxTaskAssigneeLabel(assignee, viewer.userId)}
          </Text>
        </div>
      )}
      <Text c="inherit" component="span" fz="xs">
        {getInboxTaskCreatedByLabel(creator, viewer.userId)}
      </Text>
      {task.entityType !== EntityType.USER && <InboxTaskContext entityId={task.entityId} entityType={task.entityType} />}
    </div>
  );
};

const UserDisplay = (props: UserDisplayInputProps): JSX.Element => {
  const { userId, avatar, label, prefix, isCompleted } = props;

  return (
    <>
      <Text color={!!userId ? undefined : "dimmed"} fz="sm" mr="xs" fw={500} strikethrough={isCompleted} lh={1.25}>
        {prefix}
      </Text>
      {avatar && <Avatar size={14} src={avatar} />}
      {!avatar && <IconUser size={14} />}
      <Text color={!!userId ? undefined : "dimmed"} fz="sm" ml="xs" strikethrough={isCompleted} lh={1.25}>
        {label}
      </Text>
    </>
  );
};

export const TaskCard = (props: TaskCardProps): JSX.Element => {
  const assignToTaskMutation = api.tasks.assignToUser.useMutation();
  const completeTaskMutation = api.tasks.complete.useMutation();
  const updateTaskMutation = api.tasks.update.useMutation();
  const deleteTaskMutation = api.tasks.delete.useMutation();

  const [taskModel, setTaskModel] = useState(props.task);
  const isApprovalTask = taskModel.approvalRequestId !== null;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);
  const [isUpdatingDueDate, setIsUpdatingDueDate] = useState(false);
  const [isUpdatingAssignedTo, setIsUpdatingAssignedTo] = useState(false);
  const { hovered, ref: hoverRef } = useHover();

  useEffect(() => {
    setTaskModel(props.task);
    if (props.task.approvalRequestId !== null) {
      setIsDeleting(false);
      setIsUpdatingTitle(false);
      setIsUpdatingDescription(false);
      setIsUpdatingDueDate(false);
      setIsUpdatingAssignedTo(false);
    }
  }, [props.task]);

  const now = dayjs(new Date());
  const dueDate = !!taskModel.dueDate ? dayjs(taskModel.dueDate) : null;
  let isOverdue = false;
  let dueDateLabel = "No due date";
  if (!!dueDate) {
    const dateDifference = dueDate.diff(now, "day");
    if (dateDifference === 0) {
      dueDateLabel = "Today";
    }
    if (dateDifference === 1) {
      dueDateLabel = "Tomorrow";
    }
    if (dateDifference > 1) {
      dueDateLabel = `${dateDifference} Days`;
    }
    if (dateDifference === -1) {
      dueDateLabel = "Yesterday";
      isOverdue = true;
    }
    if (dateDifference < -1) {
      dueDateLabel = `${Math.abs(dateDifference)} Days ago`;
      isOverdue = true;
    }
  }

  const assignedToLabel = useMemo<string>(() => {
    if (!taskModel.assignedTo) {
      return "Not Assigned";
    }
    return `${taskModel.assignedTo.firstName} ${taskModel.assignedTo.lastName}`;
  }, [taskModel.assignedTo]);

  const assignedToAvatar = useMemo<string | null>(() => {
    if (!taskModel.assignedTo) {
      return null;
    }
    return taskModel.assignedTo.profileImageUrl;
  }, [taskModel.assignedTo]);

  const createdByLabel = useMemo<string>(() => {
    return `${taskModel.createdBy.firstName} ${taskModel.createdBy.lastName}`;
  }, [taskModel.createdBy]);

  const createdByAvatar = useMemo<string | null>(() => {
    if (!taskModel.createdBy) {
      return null;
    }
    return taskModel.createdBy.profileImageUrl;
  }, [taskModel.createdBy]);

  const handleUpdateBase = (input: Partial<UpdateTaskSchemaType>, callback?: (task: Task) => void) => {
    if (isApprovalTask) {
      return;
    }
    updateTaskMutation.mutate(
      {
        ...taskModel,
        ...input,
        taskId: taskModel.id,
      },
      {
        onSuccess: task => {
          if (!callback) {
            return;
          }
          callback(task);
        },
      },
    );
  };

  const handleToggleComplete = (isCompleted?: boolean) => {
    if (isApprovalTask) {
      return;
    }
    completeTaskMutation.mutate(
      {
        isCompleted,
        taskId: taskModel.id,
      },
      {
        onSuccess: () => {
          props.onUpdate();
        },
      },
    );
  };

  const handleUpdateTitle = (title: string) =>
    handleUpdateBase(
      {
        title,
      },
      task => setTaskModel({ ...taskModel, title: task.title }),
    );

  const handleUpdateDescription = (description: string) =>
    handleUpdateBase(
      {
        description,
      },
      task => setTaskModel({ ...taskModel, description: task.description }),
    );

  const handleUpdateDueDate = (dueDate: Date | null) =>
    handleUpdateBase(
      {
        dueDate: dueDate,
      },
      task => setTaskModel({ ...taskModel, dueDate: task.dueDate }),
    );

  // biome-ignore-start lint/suspicious/noExplicitAny: carried over from the ESLint suppression
  const handleUpdateAssignedUser = (assignedToId: string, assignedToUser: any) => {
    // biome-ignore-end lint/suspicious/noExplicitAny: carried over from the ESLint suppression
    if (isApprovalTask) {
      return;
    }
    assignToTaskMutation.mutate(
      {
        assignedToId,
        taskId: taskModel.id,
      },
      {
        onSuccess: task => {
          setTaskModel({
            ...taskModel,
            assignedToId: task.assignedToId,
            assignedTo: { ...(assignedToUser as User), profileImageUrl: assignedToUser?.profileImage },
          });
          // Reassignment can move the task out of (or into) the viewer's own Inbox — refresh both.
          props.onUpdate();
        },
      },
    );
  };

  const handleDelete = () => {
    if (isApprovalTask) {
      return;
    }
    deleteTaskMutation.mutate(
      {
        taskId: taskModel.id,
      },
      {
        onSuccess: t => {
          showNotification({
            autoClose: 5000,
            color: "green",
            title: "Task deleted",
            message: `Your Task "${t.title}" has been successfully deleted`,
          });
          props.onUpdate();
        },
      },
    );
  };

  const enableEditing = !!props.isEditable && !taskModel.isCompleted && !isApprovalTask;
  const withChrome = props.withChrome ?? true;

  if (!withChrome) {
    const due = getInboxTaskDue(taskModel.dueDate, taskModel.isCompleted, new Date());
    const dueColors = getInboxTaskDueColors(due.tone);

    return (
      <Box ref={hoverRef} className={styles.inboxRow} bg={taskModel.isCompleted ? tokens.color.surface.canvas : tokens.color.white}>
        {hovered && !isApprovalTask && (
          <ActionIcon aria-label="Delete task" className={styles.trash} radius="sm" onClick={() => setIsDeleting(true)}>
            <IconTrash size={14} />
          </ActionIcon>
        )}
        {isDeleting && !isApprovalTask && (
          <div className={styles.confirm}>
            <Text c={tokens.color.content.primary} fz="sm">
              Permanently delete this task?
            </Text>
            <div className={styles.confirmActions}>
              <Button compact variant="outline" onClick={() => setIsDeleting(false)}>
                Cancel
              </Button>
              <Button color="red" compact onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        )}
        <div className={styles.inboxGrid}>
          <Tooltip label={`Mark as ${taskModel.isCompleted ? "incomplete" : "completed"}`} position="right" withArrow arrowSize={6}>
            <ActionIcon disabled={isApprovalTask} radius="xl" size={32} onClick={() => handleToggleComplete(!taskModel.isCompleted)}>
              {taskModel.isCompleted ? (
                <IconCircleCheckFilled color={tokens.color.content.successStrong} size={22} />
              ) : (
                <IconCircle color={tokens.color.content.muted} size={22} />
              )}
            </ActionIcon>
          </Tooltip>
          <div className={styles.titleSlot}>
            <div className={styles.titleLine}>
              <div className={styles.titleSlot}>
                {isUpdatingTitle ? (
                  <TitleUpdateInput value={taskModel.title} onChange={handleUpdateTitle} onClose={() => setIsUpdatingTitle(false)} />
                ) : (
                  <Text
                    c={taskModel.isCompleted ? tokens.color.content.secondary : tokens.color.content.strong}
                    className={classNames(enableEditing && styles.editable)}
                    fw={600}
                    fz="md"
                    lh={tokens.typography.textLineHeight.md}
                    strikethrough={taskModel.isCompleted}
                    onClick={() => {
                      if (!enableEditing) {
                        return;
                      }
                      setIsUpdatingTitle(true);
                    }}
                  >
                    {taskModel.title}
                  </Text>
                )}
              </div>
              {isUpdatingDueDate ? (
                <DueDateInput value={taskModel.dueDate} onChange={handleUpdateDueDate} onClose={() => setIsUpdatingDueDate(false)} />
              ) : (
                <Flex
                  bg={dueColors.background}
                  c={dueColors.text}
                  className={classNames(styles.duePill, enableEditing && styles.dueEditable, hovered && styles.dueShifted)}
                  onClick={() => {
                    if (!enableEditing || isUpdatingAssignedTo) {
                      return;
                    }
                    setIsUpdatingDueDate(true);
                  }}
                >
                  <IconAlarm size={13} />
                  <Text c="inherit" component="span" fw={500} fz="xs">
                    {due.label}
                  </Text>
                </Flex>
              )}
            </div>
            {isUpdatingDescription ? (
              <DescriptionUpdateInput
                value={taskModel.description ?? ""}
                onChange={handleUpdateDescription}
                onClose={() => setIsUpdatingDescription(false)}
              />
            ) : (
              (!!taskModel.description || enableEditing) && (
                <Text
                  c={tokens.color.content.secondary}
                  className={classNames(enableEditing && styles.editable)}
                  fz="sm"
                  lh={tokens.typography.textLineHeight.sm}
                  lineClamp={2}
                  strikethrough={taskModel.isCompleted}
                  onClick={() => {
                    if (!enableEditing) {
                      return;
                    }
                    setIsUpdatingDescription(true);
                  }}
                >
                  {taskModel.description || (
                    <Text c={tokens.color.content.muted} component="span" inherit>
                      Add a description
                    </Text>
                  )}
                </Text>
              )
            )}
            <InboxTaskMeta
              task={taskModel}
              enableEditing={enableEditing}
              isUpdatingAssignedTo={isUpdatingAssignedTo}
              onStartAssigning={() => setIsUpdatingAssignedTo(true)}
              onStopAssigning={() => setIsUpdatingAssignedTo(false)}
              onAssign={handleUpdateAssignedUser}
            />
          </div>
        </div>
      </Box>
    );
  }

  let cardColor = tokens.color.white;
  if (isOverdue) {
    cardColor = mantineColorRamps.danger[0];
  }
  if (taskModel.isCompleted) {
    cardColor = mantineColorRamps.gray[1];
  }

  const cardBody = (
    <>
      <Container pos="absolute" top={0} right={0} display={hovered && !isApprovalTask ? "block" : "none"}>
        <ActionIcon onClick={() => setIsDeleting(true)}>
          <IconTrash size={16} />
        </ActionIcon>
      </Container>
      {isDeleting && !isApprovalTask && (
        <Container
          h="100%"
          w="100%"
          top={0}
          left={0}
          pos="absolute"
          bg={tokens.color.white}
          style={{ border: `2px solid ${mantineColorRamps.danger[3]}`, borderRadius: tokens.radius.md, zIndex: 2 }}
        >
          <Center h="100%" w="100%" p="md">
            <Flex direction="column">
              <Text fz="sm">Are you sure you want to permanently delete this Task?</Text>
              <Center>
                <Button compact variant="subtle" onClick={() => setIsDeleting(false)}>
                  Cancel
                </Button>
                <Button color="red" compact onClick={handleDelete}>
                  Delete
                </Button>
              </Center>
            </Flex>
          </Center>
        </Container>
      )}
      <Flex align="center" gap="sm">
        <Flex>
          <Tooltip label={`Mark as ${taskModel.isCompleted ? "incomplete" : "completed"}`} position="right" withArrow arrowSize={6}>
            <ActionIcon
              disabled={isApprovalTask}
              color={taskModel.isCompleted ? "green" : undefined}
              onClick={() => handleToggleComplete(!taskModel.isCompleted)}
            >
              {taskModel.isCompleted ? <IconCircleCheckFilled /> : <IconCircle />}
            </ActionIcon>
          </Tooltip>
        </Flex>
        <Flex direction="column" style={{ flex: 1 }}>
          <Flex direction="column" mb="xs">
            {/* A task created with no entity is stamped USER/self (`createTask.ts:9,27-28`), so a USER
                badge says "no context" rather than naming one. Render the line only for a real context. */}
            {taskModel.entityType !== EntityType.USER && (
              <Text strikethrough={taskModel.isCompleted}>
                <ContextBadge entityId={taskModel.entityId} entityType={taskModel.entityType} />
              </Text>
            )}
            <Flex direction="row" mt="xs" gap="sm">
              {isUpdatingTitle ? (
                <TitleUpdateInput value={taskModel.title} onChange={handleUpdateTitle} onClose={() => setIsUpdatingTitle(false)} />
              ) : (
                <Text
                  fz="sm"
                  fw={600}
                  strikethrough={taskModel.isCompleted}
                  onClick={() => {
                    if (!enableEditing) {
                      return;
                    }
                    setIsUpdatingTitle(true);
                  }}
                  style={{ cursor: enableEditing ? "pointer " : "initial" }}
                >
                  {taskModel.title}
                </Text>
              )}
              <Flex
                align="center"
                justify="right"
                style={{ cursor: enableEditing ? "pointer " : "initial", flex: 1 }}
                onClick={() => {
                  if (!enableEditing || isUpdatingAssignedTo) {
                    return;
                  }
                  setIsUpdatingDueDate(true);
                }}
              >
                {isUpdatingDueDate ? (
                  <DueDateInput value={taskModel.dueDate} onChange={handleUpdateDueDate} onClose={() => setIsUpdatingDueDate(false)} />
                ) : (
                  <>
                    <IconAlarm size={14} />
                    <Text
                      color={isOverdue && !taskModel.isCompleted ? "red" : undefined}
                      style={{ whiteSpace: "nowrap" }}
                      fz="sm"
                      ml="xs"
                      strikethrough={taskModel.isCompleted}
                    >
                      {dueDateLabel}
                    </Text>
                  </>
                )}
              </Flex>
            </Flex>
            {isUpdatingDescription ? (
              <DescriptionUpdateInput
                value={taskModel.description ?? ""}
                onChange={handleUpdateDescription}
                onClose={() => setIsUpdatingDescription(false)}
              />
            ) : (
              <Text
                color="dimmed"
                fz="xs"
                lineClamp={2}
                strikethrough={taskModel.isCompleted}
                style={{ cursor: enableEditing ? "pointer " : "initial" }}
                w="100%"
                onClick={() => {
                  if (!enableEditing) {
                    return;
                  }
                  setIsUpdatingDescription(true);
                }}
              >
                {taskModel.description}
              </Text>
            )}
          </Flex>
          <Flex direction="row" mt="xs" gap="sm">
            <Flex
              align="center"
              style={{ cursor: enableEditing ? "pointer " : "initial", flex: 1 }}
              onClick={() => {
                if (!enableEditing || isUpdatingAssignedTo) {
                  return;
                }
                setIsUpdatingAssignedTo(true);
              }}
            >
              {isUpdatingAssignedTo ? (
                <AssignedUserSelect
                  value={taskModel.assignedToId ?? ""}
                  onClose={() => setIsUpdatingAssignedTo(false)}
                  onChange={handleUpdateAssignedUser}
                />
              ) : (
                <UserDisplay
                  userId={taskModel.assignedToId}
                  avatar={assignedToAvatar}
                  label={assignedToLabel}
                  prefix="Assigned to:"
                  isCompleted={taskModel.isCompleted}
                />
              )}
            </Flex>
          </Flex>
          <Flex direction="row" mt="xs" gap="sm">
            <Flex align="center" style={{ flex: 1 }}>
              <UserDisplay
                userId={taskModel.createdById}
                avatar={createdByAvatar}
                label={createdByLabel}
                prefix="Created by:"
                isCompleted={taskModel.isCompleted}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  );

  return (
    <Card
      bg={cardColor}
      shadow="md"
      padding="sm"
      radius="md"
      my="xs"
      withBorder
      style={{ overflow: "visible" }}
      ref={hoverRef}
      pos="relative"
    >
      {cardBody}
    </Card>
  );
};
